import React, { useRef } from 'react';
import {
  CircularProgressbarWithChildren,
  buildStyles,
} from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import { OverlayPanel } from 'primereact/overlaypanel';

import { storeDashboardAnalysis } from '../service/widget.service';

import RadialSeparators from './RadialSeparators';

const GaugeChartComponent = ({
  cubeResponse,
  target,
  annotation,
  dashboardDetails,
  dashboardLogDetails,
  compareToResponse,
  formikData,
}) => {
  const performanceType = formikData?.custom_properties?.performanceType;

  const responseKey = cubeResponse?.length
    ? Object.keys(cubeResponse[0])[0]
    : '';
  const rawValue = cubeResponse?.[0]?.[responseKey] ?? 0;

  let compareTarget = 0;

  if (compareToResponse?.length) {
    compareTarget = Object.values(compareToResponse[0])?.[0] ?? 0;
  }

  const parseNumber = valueStr => {
    if (!valueStr) return 0;
    const clean = valueStr
      .toString()
      .replace(/[^\d.,-]/g, '')
      .replace(/,/g, '')
      .replace('.', '.');
    const num = parseFloat(clean);
    return isNaN(num) ? 0 : num;
  };

  const targetRawValue =
    performanceType === 'periodComparison'
      ? parseNumber(compareTarget)
      : parseNumber(target);

  let formatter = null;
  if (annotation && Object.keys(annotation).length > 0) {
    Object.keys(annotation).forEach(key => {
      if (annotation[key]?.meta?.format) {
        formatter = eval(annotation[key].meta.format);
      }
    });
  }

  const formattedValue = formatter ? formatter(rawValue) : rawValue;
  const formattedTarget = formatter
    ? formatter(targetRawValue)
    : targetRawValue;

  const percentToTarget =
    targetRawValue > 0 ? (parseNumber(rawValue) / targetRawValue) * 100 : 0;

  const getStatusColor = percent => {
    if (percent >= 70) return 'var(--green-600)';
    if (percent >= 50) return 'var(--yellow-600)';
    return 'var(--red-600)';
  };
  const statusColor = getStatusColor(percentToTarget);

  const delta =
    targetRawValue > 0 && rawValue > 0
      ? performanceType === 'periodComparison'
        ? parseNumber(rawValue) - parseNumber(targetRawValue)
        : rawValue
      : 0;
  const outOf =
    performanceType === 'periodComparison' ? rawValue : targetRawValue;
  const deltaPercent =
    targetRawValue > 0 && rawValue > 0 ? (delta / outOf) * 100 : 0;
  const trend =
    delta === null
      ? 'neutral'
      : delta > 0
        ? 'up'
        : delta < 0
          ? 'down'
          : 'neutral';
  const trendConfig = {
    up: { color: 'var(--green-600)', icon: 'pi pi-sort-up-fill' },
    down: { color: 'var(--red-600)', icon: 'pi pi-sort-down-fill' },
    neutral: { color: 'var(--orange-600)', icon: 'pi pi-circle-fill' },
  };

  const trimLarge = num => {
    const str = num.toString();
    return str.length > 10 ? str.slice(0, 6) + '…' : str;
  };

  if (dashboardDetails && dashboardLogDetails) {
    storeDashboardAnalysis({
      ...dashboardLogDetails,
      ...dashboardDetails,
      executionTime: new Date().getTime() - dashboardDetails.start_time,
      end_time: new Date().getTime(),
    });
  }

  const tooltipTargetRef = useRef(null);

  return (
    <>
      <OverlayPanel
        ref={tooltipTargetRef}
        pt={{
          content: { className: 'p-1' },
          root: { className: 'shadow-3' },
        }}
      >
        <span className={`text-sm mt-1 flex gap-1 align-items-center`}>
          Actual: <span className="font-bold">{formattedValue}</span>{' '}
          <span className="text-sm text-gray-600">
            (Target: {formattedTarget})
          </span>
        </span>
      </OverlayPanel>
      <div
        onMouseEnter={e => tooltipTargetRef.current.show(e)}
        onMouseLeave={e => tooltipTargetRef.current.hide(e)}
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            height: '80%',
            maxWidth: '100%',
            maxHeight: '100%',
            aspectRatio: '1 / 1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgressbarWithChildren
            value={percentToTarget}
            strokeWidth={10}
            styles={buildStyles({
              pathColor: statusColor,
              trailColor: 'var(--grey-shade-2)',
              strokeLinecap: 'round',
            })}
          >
            <div
              style={{
                padding: '0px 20px',
                maxWidth: '90%',
                boxSizing: 'border-box',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: 'var(--back-shade-1)',
                }}
              >
                {trimLarge(formattedValue)} / {trimLarge(formattedTarget)}{' '}
              </div>
              {formikData?.custom_properties?.showPerformance !== false && (
                <div
                  style={{
                    fontSize: 11,
                    color: trendConfig[trend]?.color,
                    fontWeight: 600,
                  }}
                  className="flex align-items-center gap-1 justify-content-center"
                >
                  <span
                    className={trendConfig[trend]?.icon}
                    style={{ fontSize: '0.5rem' }}
                  ></span>
                  <span>{Math.abs(deltaPercent).toFixed(1)}%</span>
                </div>
              )}
            </div>

            <RadialSeparators
              count={12}
              style={{
                background: 'var(--grey-shade-5)',
                width: '2px',
                height: '7%',
              }}
            />
          </CircularProgressbarWithChildren>
        </div>
      </div>
    </>
  );
};

export default GaugeChartComponent;
